import React, { useEffect, useState } from 'react';
import { apiAdmin } from '../services/apiService';
import { apiService } from '../services/apiService';
import { SearchParameterType, ApiDetails } from '../types';
import { PageHeader } from '../components/ui/PageHeader';
import { Card } from '../components/ui/Card';
import { Button } from '../components/ui/Button';
import { Badge } from '../components/ui/Badge';
import { FormField } from '../components/ui/FormField';
import { LoadingSpinner } from '../components/ui/LoadingSpinner';
import { FieldMappingManager } from '../components/admin/FieldMappingManager';
import { MasterDataImportExport } from '../components/admin/MasterDataImportExport';
import { SimpleRapidAPIImport } from '../components/admin/SimpleRapidAPIImport';
import { QuickAPIImport } from '../components/admin/QuickAPIImport';
import { EnhancedTestModal } from '../components/admin/EnhancedTestModal';
import { APITestModal } from '../components/admin/APITestModal';
import { CloneApiModal } from '../components/admin/CloneApiModal';
import { CurlImport } from '../components/admin/CurlImport';
import { ParameterFormatGuide } from '../components/admin/ParameterFormatGuide';

// Toggle Switch Component
interface ToggleSwitchProps {
  checked: boolean;
  onChange: (checked: boolean) => void;
  disabled?: boolean;
  label?: string;
}

const ToggleSwitch: React.FC<ToggleSwitchProps> = ({ checked, onChange, disabled = false, label }) => {
  return (
    <div className="flex items-center space-x-2">
      <button
        type="button"
        onClick={() => !disabled && onChange(!checked)}
        disabled={disabled}
        className={`
          relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2
          ${checked ? 'bg-indigo-600' : 'bg-gray-200'}
          ${disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}
        `}
      >
        <span
          className={`
            inline-block h-4 w-4 transform rounded-full bg-white dark:bg-gray-800 transition-transform
            ${checked ? 'translate-x-6' : 'translate-x-1'}
          `}
        />
      </button>
      {label && (
        <span className={`text-sm ${disabled ? 'text-gray-400 dark:text-gray-500' : 'text-gray-700 dark:text-gray-200'}`}>
          {label}
        </span>
      )}
    </div>
  );
};

interface EditModalProps {
  api: ApiDetails | null;
  onSave: (data: Partial<ApiDetails>) => Promise<void>;
  onClose: () => void;
  isOpen: boolean;
  onImportSuccess?: (apiData: ApiDetails) => void;
  onSwitchTab?: (tab: string) => void;
}

const EditModal: React.FC<EditModalProps> = ({ api, onSave, onClose, isOpen, onImportSuccess, onSwitchTab }) => {
  const [formData, setFormData] = useState<Partial<ApiDetails>>({
    name: '',
    description: '',
    baseUrl: '',
    requestMethod: 'GET',
    headers: {},
    requestParameters: {},
    searchParameterTypeId: undefined,
    organizationId: undefined,
    price: 0,
    status: 'active',
    // Simplified API configuration fields
    urlPattern: '/{value}',
    parameterFormat: 'url_path',
    parameterName: '',
    // Add API documentation URL field
    apiDocumentationUrl: '',
    // Country code format fields
    countryCodeFormat: 'none',
    customCountryFormat: '',
    // Universal search flag
    isUniversalSearch: false,
    // Access control flag
    allowRegularUsers: false
  });
  const [searchParameters, setSearchParameters] = useState<SearchParameterType[]>([]);
  const [organizations, setOrganizations] = useState<any[]>([]);
  const [activeTab, setActiveTab] = useState('details');
  const [previewValue, setPreviewValue] = useState('sample');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [jsonErrors, setJsonErrors] = useState<{ headers?: string; requestParameters?: string }>({});
  const [previewTestResult, setPreviewTestResult] = useState<any>(null);
  const [isTestingPreview, setIsTestingPreview] = useState(false);
  const [randomPlaceholderSeed, setRandomPlaceholderSeed] = useState(0);
  const [verificationIdOverride, setVerificationIdOverride] = useState<string>('');

  useEffect(() => {
    const loadData = async () => {
      try {
        const [params, orgs] = await Promise.all([
          apiService.getParameterTypes(),
          apiService.getOrganizations()
        ]);
        setSearchParameters(params);
        setOrganizations(orgs);
      } catch (err) {
        console.error('Failed to load data:', err);
      }
    };
    loadData();
  }, []);

  useEffect(() => {
    if (api) {
      const getFieldValue = (camelCase: string, snakeCase: string, defaultValue: any = '') => {
        return (api as any)[camelCase] || (api as any)[snakeCase] || defaultValue;
      };
      
      setFormData({
        name: api.name || '',
        description: api.description || '',
        baseUrl: getFieldValue('baseUrl', 'base_url', ''),
        requestMethod: getFieldValue('requestMethod', 'request_method', 'GET'),
        headers: getFieldValue('headers', 'headers', {}),
        requestParameters: getFieldValue('requestParameters', 'request_parameters', {}),
        searchParameterTypeId: getFieldValue('searchParameterTypeId', 'search_parameter_type_id') || 
                              getFieldValue('searchParameterTypeId', 'search_parameter_type.id') ||
                              (api as any).search_parameter_type?.id,
        organizationId: getFieldValue('organizationId', 'organization_id') || 
                       getFieldValue('organizationId', 'organization.id') ||
                       (api as any).organization?.id,
        price: api.price || 0,
        status: api.status || 'active',
        // Simplified fields
        urlPattern: getFieldValue('urlPattern', 'url_pattern', '{value}'),
        parameterFormat: getFieldValue('parameterFormat', 'parameter_format', 'url_path'),
        parameterName: getFieldValue('parameterName', 'parameter_name', ''),
        // Add API documentation URL field
        apiDocumentationUrl: getFieldValue('apiDocumentationUrl', 'api_documentation_url', ''),
        // Country code format fields
        countryCodeFormat: getFieldValue('countryCodeFormat', 'country_code_format', 'none'),
        customCountryFormat: getFieldValue('customCountryFormat', 'custom_country_format', ''),
        // Universal search flag
        isUniversalSearch: getFieldValue('isUniversalSearch', 'is_universal_search', false),
        allowRegularUsers: getFieldValue('allowRegularUsers', 'allow_regular_users', false),
        fieldMappings: getFieldValue('fieldMappings', 'field_mappings', []),
        createdAt: getFieldValue('createdAt', 'created_at'),
        updatedAt: getFieldValue('updatedAt', 'updated_at')
      });
      setPreviewTestResult(null);
      setVerificationIdOverride('');
    } else {
      setFormData({
        name: '',
        description: '',
        baseUrl: '',
        requestMethod: 'GET',
        headers: {},
        requestParameters: {},
        searchParameterTypeId: undefined,
        organizationId: undefined,
        price: 0,
        status: 'active',
        urlPattern: '/{value}',
        parameterFormat: 'url_path',
        parameterName: '',
        apiDocumentationUrl: '',
        countryCodeFormat: 'none',
        customCountryFormat: '',
        isUniversalSearch: false,
        allowRegularUsers: false,
        fieldMappings: [],
        createdAt: undefined,
        updatedAt: undefined
      });
      setPreviewTestResult(null);
      setVerificationIdOverride('');
    }
  }, [api]);

  const validateJsonFields = (): boolean => {
    const errs: { headers?: string; requestParameters?: string } = {};
    const headersVal = formData.headers as unknown;
    if (typeof headersVal === 'string' && headersVal.trim()) {
      try {
        JSON.parse(headersVal);
      } catch {
        errs.headers = 'Headers must be valid JSON';
      }
    }
    const paramsVal = formData.requestParameters as unknown;
    if (typeof paramsVal === 'string' && paramsVal.trim()) {
      try {
        JSON.parse(paramsVal);
      } catch {
        errs.requestParameters = 'Request parameters must be valid JSON';
      }
    }
    setJsonErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const handleApplyPattern = (pattern: {
    request_method: string;
    url_pattern: string;
    parameter_format: string;
    parameter_name: string;
    request_parameters?: Record<string, string>;
    headers?: Record<string, string>;
    country_code_format?: string;
    is_universal_search?: boolean;
  }) => {
    setFormData((prev) => ({
      ...prev,
      requestMethod: pattern.request_method as ApiDetails['requestMethod'],
      urlPattern: pattern.url_pattern,
      parameterFormat: pattern.parameter_format as ApiDetails['parameterFormat'],
      parameterName: pattern.parameter_name,
      requestParameters: pattern.request_parameters ?? prev.requestParameters,
      headers: { ...(typeof prev.headers === 'object' ? prev.headers : {}), ...(pattern.headers || {}) },
      countryCodeFormat: (pattern.country_code_format as ApiDetails['countryCodeFormat']) ?? prev.countryCodeFormat,
      isUniversalSearch: pattern.is_universal_search ?? prev.isUniversalSearch,
    }));
    setActiveTab('details');
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setJsonErrors({});
    if (!validateJsonFields()) return;
    setIsLoading(true);

    try {
      const payload: Partial<ApiDetails> = { ...formData };
      if (formData.isUniversalSearch && (formData.searchParameterTypeId === undefined || formData.searchParameterTypeId === null)) {
        (payload as any).searchParameterTypeId = null;
      }
      await onSave(payload);
      // Keep form open after save so user can continue editing or test
    } catch (err: any) {
      setError(err.message || 'Failed to save API');
    } finally {
      setIsLoading(false);
    }
  };

  const handleJsonChange = (field: string, value: string) => {
    try {
      const parsed = JSON.parse(value);
      setFormData(prev => ({ ...prev, [field]: parsed }));
    } catch (e) {
      // Keep the string value if JSON is invalid
      setFormData(prev => ({ ...prev, [field]: value }));
    }
  };

  const formatJson = (obj: any) => {
    if (typeof obj === 'string') return obj;
    return JSON.stringify(obj, null, 2);
  };

  // Derive country_code from preview value if it looks like +CC..., else use as-is
  const previewCountryCode = (() => {
    const v = previewValue.trim();
    const plusMatch = v.match(/^\+(\d{1,4})/);
    if (plusMatch) return plusMatch[1];
    if (/^\d{1,4}/.test(v)) return v.replace(/\D/g, '').slice(0, 4) || v;
    return v || '91';
  })();

  const valueToUse = previewValue.trim() || 'sample';
  const phoneToUse = previewValue.trim() || '+919876543210';

  // Generate UUID and random string for {uuid}, {random} placeholders (stable until "Generate" is clicked)
  const [uuidForPlaceholder, randomForPlaceholder] = React.useMemo(() => {
    const uuid = typeof crypto !== 'undefined' && crypto.randomUUID
      ? crypto.randomUUID()
      : 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
          const r = (Math.random() * 16) | 0;
          const v = c === 'x' ? r : (r & 0x3) | 0x8;
          return v.toString(16);
        });
    const random = Array.from({ length: 16 }, () => 'abcdefghijklmnopqrstuvwxyz0123456789'[Math.floor(Math.random() * 36)]).join('');
    return [uuid, random];
  }, [randomPlaceholderSeed]);
  const verificationIdForReplace = verificationIdOverride?.trim() ? verificationIdOverride.trim() : uuidForPlaceholder;

  // Normalize requestParameters to an object (form may have string from textarea when JSON invalid)
  const getRequestParamsAsObject = (): Record<string, unknown> => {
    const r: unknown = formData.requestParameters;
    if (typeof r === 'object' && r != null && !Array.isArray(r)) return { ...r } as Record<string, unknown>;
    if (typeof r === 'string' && r.trim()) {
      try {
        const parsed = JSON.parse(r) as Record<string, unknown>;
        return typeof parsed === 'object' && parsed != null ? parsed : {};
      } catch {
        return {};
      }
    }
    return {};
  };

  // Replace {value}, {phone_number}, {country_code}, {uuid}, {random}, {verification_id} placeholders (used for URL, headers, body)
  const replacePlaceholders = (obj: unknown, valueStr: string, phoneStr: string, countryStr: string): unknown => {
    const repl = (s: string) =>
      s
        .replace(/\{value\}/g, valueStr)
        .replace(/\{phone_number\}/g, phoneStr)
        .replace(/\{country_code\}/g, countryStr)
        .replace(/\{uuid\}/g, uuidForPlaceholder)
        .replace(/\{random\}/g, randomForPlaceholder)
        .replace(/\{verification_id\}/g, verificationIdForReplace);
    if (typeof obj === 'string') return repl(obj);
    if (Array.isArray(obj)) return obj.map((item) => replacePlaceholders(item, valueStr, phoneStr, countryStr));
    if (obj && typeof obj === 'object') {
      const out: Record<string, unknown> = {};
      for (const [k, v] of Object.entries(obj)) {
        out[k] = replacePlaceholders(v, valueStr, phoneStr, countryStr);
      }
      return out;
    }
    return obj;
  };

  // Build query string from requestParameters + optional paramName=value (for query_param / custom GET)
  const buildQueryString = (includeSearchValue: boolean) => {
    const params = getRequestParamsAsObject();
    const paramName = (formData.parameterName || 'q').trim();
    const queryObj = replacePlaceholders(JSON.parse(JSON.stringify(params)), valueToUse, phoneToUse, previewCountryCode) as Record<string, unknown>;
    if (includeSearchValue && paramName) (queryObj as Record<string, string>)[paramName] = valueToUse;
    const search = new URLSearchParams();
    Object.entries(queryObj).forEach(([k, v]) => {
      if (v != null && v !== '') search.set(k, String(v));
    });
    return search.toString();
  };

  // Build preview request URL from form data (all parameter formats: url_path, query_param, body_json, form_data, custom)
  const previewRequestUrl = (() => {
    const base = (formData.baseUrl || '').replace(/\/+$/, '');
    if (!base) return '(Enter Base URL)';
    const format = formData.parameterFormat || 'url_path';
    const pattern = (formData.urlPattern ?? '/{value}').trim();
    const path = pattern.startsWith('/') ? pattern : pattern ? `/${pattern}` : '';

    const pathWithPlaceholders = path
      .replace(/\{value\}/g, valueToUse)
      .replace(/\{phone_number\}/g, phoneToUse)
      .replace(/\{country_code\}/g, previewCountryCode)
      .replace(/\{uuid\}/g, uuidForPlaceholder)
      .replace(/\{random\}/g, randomForPlaceholder)
      .replace(/\{verification_id\}/g, verificationIdForReplace);
    const baseWithPath = pathWithPlaceholders ? `${base}${pathWithPlaceholders}` : base;

    switch (format) {
      case 'query_param': {
        const qs = buildQueryString(true);
        return qs ? `${baseWithPath}${baseWithPath.includes('?') ? '&' : '?'}${qs}` : baseWithPath;
      }
      case 'custom': {
        // Custom: path with placeholders; GET uses request_parameters as query string
        const method = formData.requestMethod || 'GET';
        if (method === 'GET' || method === 'DELETE') {
          const qs = buildQueryString(true);
          return qs ? `${baseWithPath}${baseWithPath.includes('?') ? '&' : '?'}${qs}` : baseWithPath;
        }
        return baseWithPath;
      }
      case 'url_path':
      case 'body_json':
      case 'form_data':
      default:
        return path ? `${base}${pathWithPlaceholders}` : base;
    }
  })();

  // Build preview request body (respects parameter format + request method)
  const previewRequestBody = (() => {
    const method = formData.requestMethod || 'GET';
    const format = formData.parameterFormat || 'url_path';
    const params = getRequestParamsAsObject();

    if (method === 'GET' || method === 'DELETE') {
      return null;
    }

    const paramName = (formData.parameterName || 'q').trim();
    const buildBody = (): Record<string, unknown> => {
      const base = { ...params };
      const body = replacePlaceholders(JSON.parse(JSON.stringify(base)), valueToUse, phoneToUse, previewCountryCode) as Record<string, unknown>;
      // When no request params (or empty), add parameterName -> value (same as backend)
      if (Object.keys(params).length === 0 && paramName) {
        body[paramName] = valueToUse;
      }
      return body;
    };

    switch (format) {
      case 'body_json': {
        const body = buildBody();
        return JSON.stringify(body, null, 2);
      }
      case 'form_data': {
        const body = buildBody();
        return Object.entries(body)
          .map(([k, v]) => `${k}=${encodeURIComponent(String(v))}`)
          .join('&');
      }
      case 'custom': {
        const body = buildBody();
        return JSON.stringify(body, null, 2);
      }
      case 'url_path':
      case 'query_param':
      default:
        return null;
    }
  })();

  // Headers with placeholders replaced (same as will be sent)
  const previewRequestHeaders = (() => {
    const raw = formData.headers;
    const obj = typeof raw === 'object' && raw != null ? { ...raw } : {};
    const replaced = replacePlaceholders(obj, valueToUse, phoneToUse, previewCountryCode) as Record<string, string>;
    return typeof raw === 'object' && raw != null ? replaced : {};
  })();

  // Exact request sent when you click Test API — built from form (method, URL pattern, parameter format, headers, request parameters + preview value)
  const completeRequestPayload = (() => {
    const method = (formData.requestMethod || 'GET').toUpperCase();
    const format = formData.parameterFormat || 'url_path';
    const url = previewRequestUrl.startsWith('(') ? '' : previewRequestUrl;
    const headers: Record<string, string> = {};
    Object.entries(previewRequestHeaders).forEach(([k, v]) => {
      if (v != null && typeof v === 'string') headers[k] = v;
    });
    if (format === 'form_data' && previewRequestBody != null) {
      const hasContentType = Object.keys(headers).some((k) => k.toLowerCase() === 'content-type');
      if (!hasContentType) headers['Content-Type'] = 'application/x-www-form-urlencoded';
    }
    const result: Record<string, unknown> = { method, url, headers };
    const methodHasBody = method !== 'GET' && method !== 'DELETE';
    if (methodHasBody) {
      if (previewRequestBody != null && previewRequestBody !== '') {
        if (format === 'form_data') {
          result.body = previewRequestBody;
        } else {
          try {
            result.body = JSON.parse(previewRequestBody) as object;
          } catch {
            result.body = previewRequestBody;
          }
        }
      } else {
        result.body = null;
      }
    }
    return result;
  })();

  // Display in Python-like format so method, url, headers and payload/body are always clearly visible
  const completeRequestText = (() => {
    if (!formData.baseUrl?.trim()) return 'Fill Base URL and preview value to see the complete request.';
    const method = completeRequestPayload.method as string;
    const url = completeRequestPayload.url as string;
    const headers = completeRequestPayload.headers as Record<string, string> | undefined;
    const body = completeRequestPayload.body;
    const lines: string[] = [
      `method = "${method}"`,
      `url = "${url}"`,
      `headers = ${JSON.stringify(headers || {}, null, 2)}`,
    ];
    if (method !== 'GET' && method !== 'DELETE') {
      lines.push(`payload = ${typeof body === 'object' && body !== null ? JSON.stringify(body, null, 2) : body === null ? 'None' : JSON.stringify(body)}`);
    }
    return lines.join('\n');
  })();

  const handleTestFromPreview = async () => {
    if (!formData.baseUrl?.trim()) {
      setError('Enter Base URL before testing.');
      return;
    }
    if (completeRequestPayload.url === '') {
      setError('Request URL could not be built. Check Base URL and URL Pattern.');
      return;
    }
    setPreviewTestResult(null);
    setIsTestingPreview(true);
    setError(null);
    try {
      const result = await apiAdmin.executeRawRequest({
        method: completeRequestPayload.method as string,
        url: completeRequestPayload.url as string,
        headers: (completeRequestPayload.headers || {}) as Record<string, string>,
        body: (completeRequestPayload.body !== undefined && completeRequestPayload.body !== null
          ? completeRequestPayload.body
          : undefined) as string | object | undefined,
      });
      setPreviewTestResult({
        success: result.success,
        message: result.message,
        test_result: {
          response_status: result.response_status,
          response_time: result.response_time,
          response: result.response,
          error: result.message && !result.success ? result.message : undefined,
        },
      });
    } catch (err: any) {
      setPreviewTestResult({ success: false, message: err.message || 'Test failed' });
    } finally {
      setIsTestingPreview(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-2 sm:p-4 animate-in fade-in duration-200">
      <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-2xl border border-gray-100 dark:border-gray-700/80 w-full max-w-4xl max-h-[95vh] sm:max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header - Mobile Optimized */}
        <div className="px-4 sm:px-6 py-3 sm:py-4 border-b border-gray-200 dark:border-gray-700 flex-shrink-0">
          <div className="flex items-center justify-between">
            <h2 className="text-lg sm:text-xl font-semibold text-gray-900 dark:text-white truncate">
              {api ? 'Edit API' : 'Create New API'}
            </h2>
            <Button variant="ghost" onClick={onClose} className="flex-shrink-0">
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </Button>
          </div>
        </div>
        
        {/* Scrollable Content */}
        <div className="flex-1 overflow-y-auto">
          <form onSubmit={handleSubmit} className="space-y-6 p-4 sm:p-6" id="edit-api-form">
            {/* Tab Navigation - Mobile Optimized */}
          <div className="border-b border-gray-200 dark:border-gray-700 mb-6">
              <nav className="-mb-px flex space-x-2 sm:space-x-8 overflow-x-auto">
              {[
                { id: 'details', label: 'Basic & Configuration' },
                { id: 'curl', label: 'cURL Import' },
                { id: 'rapidapi', label: 'RapidAPI' },
                { id: 'quickimport', label: 'Quick Import' },
                { id: 'patterns', label: 'Patterns' },
              ].map((tab) => (
                <button
                  key={tab.id}
                  type="button"
                  onClick={() => setActiveTab(tab.id)}
                    className={`py-2 px-2 sm:px-1 border-b-2 font-medium text-xs sm:text-sm whitespace-nowrap ${
                    activeTab === tab.id
                      ? 'border-indigo-500 text-indigo-600 dark:text-indigo-400'
                      : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 hover:border-gray-300 dark:border-gray-600'
                  }`}
                >
                  {tab.label}
                </button>
              ))}
            </nav>
          </div>

          {/* Details Tab: Basic + Configuration merged */}
          {activeTab === 'details' && (
              <div className="space-y-4 sm:space-y-6">
                {!api && (
                  <div className="bg-indigo-50 dark:bg-indigo-900/20 border border-indigo-200 rounded-lg p-4">
                    <p className="text-sm font-medium text-indigo-800 mb-1">Add an API and it will be used in searches immediately</p>
                    <p className="text-xs text-indigo-700">Use <strong>cURL Import</strong>, <strong>RapidAPI</strong>, or <strong>Quick Import</strong> tabs for one-click setup, or fill the form below manually. Save as Active to include in search runs.</p>
                  </div>
                )}
                <ParameterFormatGuide onApplyPattern={handleApplyPattern} compact />
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-6">
                <FormField label="API Name *">
                  <input
                    type="text"
                    value={formData.name}
                    onChange={(e) => setFormData({...formData, name: e.target.value})}
                      className="w-full px-3 sm:px-4 py-2 sm:py-3 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 text-sm sm:text-base"
                    placeholder="Enter API name"
                    required
                  />
                </FormField>

                <FormField label="Base URL *">
                  <input
                    type="url"
                    value={formData.baseUrl}
                    onChange={(e) => setFormData({...formData, baseUrl: e.target.value})}
                      className="w-full px-3 sm:px-4 py-2 sm:py-3 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 text-sm sm:text-base"
                    placeholder="https://api.example.com"
                    required
                  />
                </FormField>

                  <FormField label="API Documentation URL">
                    <input
                      type="url"
                      value={formData.apiDocumentationUrl || ''}
                      onChange={(e) => setFormData({...formData, apiDocumentationUrl: e.target.value})}
                      className="w-full px-3 sm:px-4 py-2 sm:py-3 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 text-sm sm:text-base"
                      placeholder="https://docs.example.com/api"
                    />
                    <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">Link to API documentation or webpage</p>
                  </FormField>

                <FormField label="Request Method *">
                  <select
                    value={formData.requestMethod}
                    onChange={(e) => setFormData({...formData, requestMethod: e.target.value as any})}
                    className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                    required
                  >
                    <option value="GET">GET</option>
                    <option value="POST">POST</option>
                    <option value="PUT">PUT</option>
                    <option value="PATCH">PATCH</option>
                    <option value="DELETE">DELETE</option>
                  </select>
                </FormField>

                <FormField label="URL Pattern *">
                  <input
                    type="text"
                    value={formData.urlPattern}
                    onChange={(e) => setFormData({...formData, urlPattern: e.target.value})}
                    className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                    placeholder="/search/{value} or /query?q={value}"
                    required
                  />
                  <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">Path or query; use {'{value}'}, {'{phone_number}'}, {'{country_code}'}</p>
                </FormField>

                <FormField label="Parameter Format *">
                  <select
                    value={formData.parameterFormat}
                    onChange={(e) => setFormData({...formData, parameterFormat: e.target.value as any})}
                    className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                    required
                  >
                    <option value="url_path">URL Path</option>
                    <option value="query_param">Query Parameter</option>
                    <option value="body_json">JSON Body</option>
                    <option value="form_data">Form Data</option>
                    <option value="custom">Custom Format</option>
                  </select>
                </FormField>

                <FormField label="Parameter Name">
                  <input
                    type="text"
                    value={formData.parameterName}
                    onChange={(e) => setFormData({...formData, parameterName: e.target.value})}
                    className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                    placeholder="q, search, phone, etc."
                  />
                  <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">For query_param / body_json / form_data</p>
                </FormField>

                <FormField label="Price (Int Coins)">
                  <input
                    type="number"
                    value={formData.price}
                    onChange={(e) => setFormData({...formData, price: parseInt(e.target.value) || 0})}
                    className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                    min="0"
                    placeholder="0"
                  />
                </FormField>

                <FormField label="Universal Search">
                  <div className="flex items-center space-x-3">
                    <input
                      type="checkbox"
                      id="isUniversalSearch"
                      checked={formData.isUniversalSearch || false}
                      onChange={(e) => setFormData({...formData, isUniversalSearch: e.target.checked})}
                      className="h-4 w-4 text-indigo-600 dark:text-indigo-400 focus:ring-indigo-500 border-gray-300 dark:border-gray-600 rounded"
                    />
                    <label htmlFor="isUniversalSearch" className="text-sm text-gray-700 dark:text-gray-200">
                      This API works with <strong>all parameter types</strong> (universal search)
                    </label>
                  </div>
                  <p className="text-xs text-gray-500 dark:text-gray-400 mt-2">
                    ℹ️ When enabled, this API will be used for searches of any parameter type. Leave unchecked for type-specific APIs.
                  </p>
                </FormField>

                <FormField label="Allow Regular Users">
                  <div className="flex items-center space-x-3">
                    <input
                      type="checkbox"
                      id="allowRegularUsers"
                      checked={formData.allowRegularUsers || false}
                      onChange={(e) => setFormData({...formData, allowRegularUsers: e.target.checked})}
                      className="h-4 w-4 text-indigo-600 dark:text-indigo-400 focus:ring-indigo-500 border-gray-300 dark:border-gray-600 rounded"
                    />
                    <label htmlFor="allowRegularUsers" className="text-sm text-gray-700 dark:text-gray-200">
                      When enabled, <strong>regular users</strong> can access this API.
                    </label>
                  </div>
                  <p className="text-xs text-gray-500 dark:text-gray-400 mt-2">
                    Law enforcement users can access all active APIs by default.
                  </p>
                </FormField>

                <FormField label={formData.isUniversalSearch ? "Primary Search Parameter Type (Optional)" : "Search Parameter Type *"}>
                  <select
                    value={formData.searchParameterTypeId ?? ''}
                    onChange={(e) => {
                      const val = e.target.value;
                      setFormData({ ...formData, searchParameterTypeId: val === '' ? undefined : parseInt(val, 10) });
                    }}
                    className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                    required={!formData.isUniversalSearch}
                  >
                    <option value="">Select parameter type</option>
                    {searchParameters.map(param => (
                      <option key={param.id} value={param.id}>
                        {param.display_name}
                      </option>
                    ))}
                  </select>
                  {formData.isUniversalSearch && (
                    <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                      Optional: Select a primary type for categorization purposes only
                    </p>
                  )}
                </FormField>

                <FormField label="Organization *">
                  <select
                    value={formData.organizationId ?? ''}
                    onChange={(e) => {
                      const val = e.target.value;
                      setFormData({ ...formData, organizationId: val === '' ? undefined : parseInt(val, 10) });
                    }}
                    className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                    required
                  >
                    <option value="">Select organization</option>
                    {organizations.map(org => (
                      <option key={org.id} value={org.id}>
                        {org.name}
                      </option>
                    ))}
                  </select>
                </FormField>

                <FormField label="Country Code Format">
                  <select
                    value={formData.countryCodeFormat}
                    onChange={(e) => setFormData({...formData, countryCodeFormat: e.target.value as any})}
                    className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                  >
                    <option value="none">No Country Code</option>
                    <option value="numeric">Numeric (91)</option>
                    <option value="plus_prefix">Plus Prefix (+91)</option>
                    <option value="iso_code">ISO Code (IN)</option>
                    <option value="custom">Custom Format</option>
                  </select>
                </FormField>

                {formData.countryCodeFormat === 'custom' && (
                  <FormField label="Custom Country Format">
                    <input
                      type="text"
                      value={formData.customCountryFormat}
                      onChange={(e) => setFormData({...formData, customCountryFormat: e.target.value})}
                      className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                      placeholder="+{country_code}, etc."
                    />
                  </FormField>
                )}
              </div>

              <FormField label="Description">
                <textarea
                  value={formData.description}
                  onChange={(e) => setFormData({...formData, description: e.target.value})}
                  className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                  rows={3}
                  placeholder="Enter API description"
                />
              </FormField>

              <FormField label="Headers (JSON)">
                <textarea
                  value={formatJson(formData.headers)}
                  onChange={(e) => { handleJsonChange('headers', e.target.value); setJsonErrors(prev => ({ ...prev, headers: undefined })); }}
                  className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 font-mono text-sm ${jsonErrors.headers ? 'border-red-500' : 'border-gray-300 dark:border-gray-600'}`}
                  rows={4}
                  placeholder='{"Content-Type": "application/json", "Authorization": "Bearer token"}'
                />
                {jsonErrors.headers && <p className="text-sm text-red-600 dark:text-red-400 mt-1">{jsonErrors.headers}</p>}
              </FormField>

              <FormField label="Request Parameters (JSON)">
                <textarea
                  value={formatJson(formData.requestParameters)}
                  onChange={(e) => { handleJsonChange('requestParameters', e.target.value); setJsonErrors(prev => ({ ...prev, requestParameters: undefined })); }}
                  className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 font-mono text-sm ${jsonErrors.requestParameters ? 'border-red-500' : 'border-gray-300 dark:border-gray-600'}`}
                  rows={4}
                  placeholder='{"verification_id": "{uuid}", "ifsc": "{value}"}'
                />
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">Use placeholders: {'{value}'}, {'{uuid}'}, {'{random}'}, {'{verification_id}'}, {'{phone_number}'}, {'{country_code}'}</p>
                {jsonErrors.requestParameters && <p className="text-sm text-red-600 dark:text-red-400 mt-1">{jsonErrors.requestParameters}</p>}
              </FormField>

              {/* Request Preview & Test (Postman-like) */}
              <div className="border border-gray-200 dark:border-gray-700 rounded-lg overflow-hidden">
                <div className="bg-gray-100 dark:bg-gray-800 px-4 py-2 border-b border-gray-200 dark:border-gray-700">
                  <h3 className="text-sm font-semibold text-gray-800 dark:text-gray-100">Test request (like Postman)</h3>
                  <p className="text-xs text-gray-600 dark:text-gray-300 mt-0.5">Placeholders: {'{value}'}, {'{uuid}'}, {'{random}'}, {'{verification_id}'}, {'{phone_number}'}, {'{country_code}'}. Use Generate to refresh random values.</p>
                </div>
                <div className="p-4 space-y-4 bg-gray-50">
                  <FormField label="Preview value (used for {value} in URL and body)">
                    <input
                      type="text"
                      value={previewValue}
                      onChange={(e) => setPreviewValue(e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 text-sm"
                      placeholder="e.g. sample, HDFC0000001, +919876543210"
                    />
                  </FormField>
                  <FormField label="Verification ID (optional — used for {verification_id})">
                    <div className="flex gap-2">
                      <input
                        type="text"
                        value={verificationIdOverride}
                        onChange={(e) => setVerificationIdOverride(e.target.value)}
                        className="flex-1 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 text-sm font-mono"
                        placeholder="Leave empty to use generated UUID, or type / generate one"
                      />
                      <Button
                        type="button"
                        variant="outline"
                        onClick={() => {
                          const newUuid = typeof crypto !== 'undefined' && crypto.randomUUID ? crypto.randomUUID() : 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => { const r = (Math.random() * 16) | 0; return (c === 'x' ? r : (r & 0x3) | 0x8).toString(16); });
                          setVerificationIdOverride(newUuid);
                          setRandomPlaceholderSeed((s) => s + 1);
                        }}
                      >
                        Generate
                      </Button>
                    </div>
                    <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">Generate a new random ID and refresh {'{uuid}'} / {'{random}'} in the request.</p>
                  </FormField>

                  {/* Request: method + URL bar */}
                  <div className="flex flex-wrap items-center gap-2">
                    <select
                      className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg font-mono text-sm font-medium bg-white dark:bg-gray-800 min-w-[100px]"
                      value={formData.requestMethod || 'GET'}
                      onChange={(e) => setFormData({ ...formData, requestMethod: e.target.value as any })}
                    >
                      <option value="GET">GET</option>
                      <option value="POST">POST</option>
                      <option value="PUT">PUT</option>
                      <option value="PATCH">PATCH</option>
                      <option value="DELETE">DELETE</option>
                    </select>
                    <div className="flex-1 min-w-0 font-mono text-sm text-gray-700 dark:text-gray-200 break-all bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg px-3 py-2">
                      {previewRequestUrl.startsWith('(') ? previewRequestUrl : previewRequestUrl}
                    </div>
                  </div>

                  {/* Request headers (with placeholders replaced — what will be sent) */}
                  <div>
                    <span className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide">Headers (sent with request)</span>
                    <pre className="mt-1 p-3 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded font-mono text-sm text-gray-800 dark:text-gray-100 overflow-x-auto">
                      {Object.keys(previewRequestHeaders).length ? JSON.stringify(previewRequestHeaders, null, 2) : '(none)'}
                    </pre>
                  </div>
                  <div>
                    <span className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide">Body (for POST/PUT/PATCH)</span>
                    <pre className="mt-1 p-3 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded font-mono text-sm text-gray-800 dark:text-gray-100 overflow-x-auto min-h-[60px]">
                      {previewRequestBody ?? '(No body — GET/DELETE or URL/query only)'}
                    </pre>
                  </div>

                  <FormField label="Complete request (used for Test API)">
                    <textarea
                      readOnly
                      value={completeRequestText}
                      className="w-full px-3 py-2 border border-gray-200 dark:border-gray-700 rounded font-mono text-xs bg-white dark:bg-gray-800 text-gray-800 dark:text-gray-100 overflow-x-auto resize-y min-h-[100px]"
                      rows={6}
                      spellCheck={false}
                    />
                  </FormField>

                  <div className="pt-4 border-t border-gray-200 dark:border-gray-700">
                    <Button
                      type="button"
                      variant="primary"
                      onClick={handleTestFromPreview}
                      disabled={isTestingPreview || !formData.baseUrl?.trim()}
                    >
                      {isTestingPreview ? 'Sending...' : 'Send request (Test API)'}
                    </Button>

                    {previewTestResult && (
                      <div className="mt-4 rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 overflow-hidden">
                        <div className="px-4 py-2 bg-gray-100 dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700 flex flex-wrap items-center gap-3">
                          <span className="font-semibold text-gray-800 dark:text-gray-100">Response</span>
                          {previewTestResult.test_result?.response_status != null && (
                            <span
                              className={`px-2 py-0.5 rounded text-sm font-mono font-medium ${
                                (previewTestResult.test_result.response_status as number) >= 200 && (previewTestResult.test_result.response_status as number) < 300
                                  ? 'bg-green-100 dark:bg-green-900/30 text-green-800'
                                  : (previewTestResult.test_result.response_status as number) >= 400 && (previewTestResult.test_result.response_status as number) < 500
                                  ? 'bg-amber-100 text-amber-800'
                                  : (previewTestResult.test_result.response_status as number) >= 500
                                  ? 'bg-red-100 dark:bg-red-900/30 text-red-800'
                                  : 'bg-blue-100 dark:bg-blue-900/30 text-blue-800'
                              }`}
                            >
                              {previewTestResult.test_result.response_status}
                            </span>
                          )}
                          {previewTestResult.test_result?.response_time != null && (
                            <span className="text-xs text-gray-500 dark:text-gray-400">{previewTestResult.test_result.response_time.toFixed(2)}s</span>
                          )}
                          {!previewTestResult.success && previewTestResult.message && (
                            <span className="text-sm text-red-600 dark:text-red-400">{previewTestResult.message}</span>
                          )}
                        </div>
                        <div className="p-4">
                          <span className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide">Response body</span>
                          <pre className="mt-1 p-3 bg-gray-50 border border-gray-200 dark:border-gray-700 rounded font-mono text-xs text-gray-800 dark:text-gray-100 overflow-auto max-h-64 whitespace-pre-wrap break-words">
                            {(() => {
                              const resp = previewTestResult.test_result?.response;
                              const errMsg = previewTestResult.message;
                              if (resp == null && errMsg) return errMsg;
                              if (resp == null) return '(empty)';
                              const data = typeof resp === 'object' && resp !== null && 'data' in resp ? (resp as { data?: unknown }).data : resp;
                              if (data === undefined || data === null) return errMsg || '(empty)';
                              return typeof data === 'object' ? JSON.stringify(data, null, 2) : String(data);
                            })()}
                          </pre>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* cURL Import Tab */}
          {activeTab === 'curl' && (
            <div className="space-y-6">
              <CurlImport
                onImport={(apiData) => {
                  if (onImportSuccess) {
                    onImportSuccess(apiData);
                  }
                  setActiveTab('details');
                }}
                onCancel={() => onClose()}
              />
            </div>
          )}

          {/* RapidAPI Import Tab */}
          {activeTab === 'rapidapi' && (
            <div className="space-y-6">
              <SimpleRapidAPIImport 
                onImport={(apiData) => {
                  if (onImportSuccess) {
                    onImportSuccess(apiData);
                  }
                  // Switch to basic tab to show imported data
                  setActiveTab('details');
                }} 
                onCancel={() => onClose()} 
              />
            </div>
          )}

          {/* Quick Import Tab */}
          {activeTab === 'quickimport' && (
            <div className="space-y-6">
              <QuickAPIImport 
                onImport={(apiData) => {
                  if (onImportSuccess) {
                    onImportSuccess(apiData);
                  }
                  // Switch to basic tab to show imported data
                  setActiveTab('details');
                }} 
                onCancel={() => onClose()} 
              />
            </div>
          )}

          {/* Integration Patterns Tab */}
          {activeTab === 'patterns' && (
            <div className="space-y-4">
              <ParameterFormatGuide onApplyPattern={handleApplyPattern} />
              <p className="text-xs text-gray-500">Click Apply on any pattern to populate the Basic & Configuration form.</p>
            </div>
          )}

          {error && (
              <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-md p-3 sm:p-4">
              <p className="text-sm text-red-600 dark:text-red-400">{error}</p>
            </div>
          )}
          </form>
        </div>

        {/* Fixed Footer with Save Buttons - Mobile Optimized */}
        <div className="flex-shrink-0 border-t border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 p-4 sm:p-6">
          <div className="flex flex-col sm:flex-row justify-end space-y-3 sm:space-y-0 sm:space-x-3">
            <Button 
              type="button" 
              variant="secondary" 
              onClick={onClose}
              className="w-full sm:w-auto order-2 sm:order-1"
            >
              Cancel
            </Button>
            <Button 
              type="submit" 
              disabled={isLoading}
              form="edit-api-form"
              className="w-full sm:w-auto order-1 sm:order-2"
            >
              {isLoading ? (
                <>
                  <LoadingSpinner size="sm" className="mr-2" />
                  Saving...
                </>
              ) : (
                api ? 'Update API' : 'Create API'
              )}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

interface ConfirmModalProps {
  isOpen: boolean;
  title: string;
  message: string;
  onConfirm: () => void;
  onCancel: () => void;
}

const ConfirmModal: React.FC<ConfirmModalProps> = ({ isOpen, title, message, onConfirm, onCancel }) => {
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-md w-full">
        <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white">{title}</h3>
        </div>
        <div className="px-6 py-4">
          <p className="text-gray-600 dark:text-gray-300">{message}</p>
        </div>
        <div className="px-6 py-4 border-t border-gray-200 dark:border-gray-700 flex justify-end space-x-3">
          <Button variant="secondary" onClick={onCancel}>
            Cancel
          </Button>
          <Button variant="danger" onClick={onConfirm}>
            Confirm
          </Button>
        </div>
      </div>
    </div>
  );
};

const AdminApiManagementPage: React.FC = () => {
  const [apis, setApis] = useState<ApiDetails[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [selectedApi, setSelectedApi] = useState<ApiDetails | null>(null);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showTestModal, setShowTestModal] = useState(false);
  const [showAPITestModal, setShowAPITestModal] = useState(false);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [showFieldMappingModal, setShowFieldMappingModal] = useState(false);
  const [confirmAction, setConfirmAction] = useState<(() => void) | null>(null);
  const [testResult, setTestResult] = useState<any>(null);
  const [sortField, setSortField] = useState<string>('name');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('asc');
  const [showBulkConfirmModal, setShowBulkConfirmModal] = useState(false);
  const [bulkAction, setBulkAction] = useState<'activate' | 'deactivate' | null>(null);
  const [selectedOrganization, setSelectedOrganization] = useState<string>('all');
  const [selectedParameterType, setSelectedParameterType] = useState<string>('all');
  const [organizations, setOrganizations] = useState<any[]>([]);
  const [showCloneModal, setShowCloneModal] = useState(false);
  const [searchParameters, setSearchParameters] = useState<SearchParameterType[]>([]);

  const fetchApis = async () => {
    try {
      setLoading(true);
      const [apiList, orgList, paramTypes] = await Promise.all([
        apiAdmin.listApis(),
        apiService.getOrganizations(),
        apiService.getSearchParameters()
      ]);
      console.log('Fetched APIs:', apiList.length, 'APIs');
      setApis(apiList);
      setOrganizations(orgList);
      setSearchParameters(paramTypes);
    } catch (err: any) {
      console.error('Error fetching APIs:', err);
      setError(err.message || 'Failed to load APIs');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchApis();
  }, []);

  const handleEdit = async (api: ApiDetails) => {
    try {
      // Fetch fresh data from database
      setLoading(true);
      const freshApiData = await apiAdmin.getApi(api.id!);
      console.log('Fetched fresh API data from database:', freshApiData);
      setSelectedApi(freshApiData);
      setShowEditModal(true);
    } catch (err: any) {
      console.error('Failed to fetch API details:', err);
      setError('Failed to load API details');
      // Fallback to cached data if fetch fails
      setSelectedApi(api);
      setShowEditModal(true);
    } finally {
      setLoading(false);
    }
  };

  const handleAPITest = (api: ApiDetails) => {
    setSelectedApi(api);
    setShowAPITestModal(true);
  };

  const handleCreate = () => {
    setSelectedApi(null);
    setShowEditModal(true);
  };

  const handleClone = (api: ApiDetails) => {
    setSelectedApi(api);
    setShowCloneModal(true);
  };

  const handleCloneSubmit = async (clonedData: Partial<ApiDetails>) => {
    try {
      const newApi = await apiAdmin.createApi(clonedData);
      setApis(prevApis => [...prevApis, newApi]);
      setShowCloneModal(false);
      setSelectedApi(null);
      // Show success message or notification
      alert(`API "${newApi.name}" cloned successfully!`);
    } catch (err: any) {
      console.error('Failed to clone API:', err);
      throw new Error(err.message || 'Failed to clone API');
    }
  };

  const handleSave = async (data: Partial<ApiDetails>) => {
    try {
      if (selectedApi) {
        const updatedApi = await apiAdmin.updateApi(selectedApi.id!, data);
        // Update the selected API with the response data
        setSelectedApi(updatedApi);
        // Also update the API in the local list
        setApis(prevApis => 
          prevApis.map(api => 
            api.id === selectedApi.id ? { ...api, ...updatedApi } : api
          )
        );
        console.log('API updated successfully:', updatedApi);
      } else {
        const newApi = await apiAdmin.createApi(data);
        // Add the new API to the list
        setApis(prevApis => [...prevApis, newApi]);
        console.log('API created successfully:', newApi);
      }
    } catch (err: any) {
      console.error('Failed to save API:', err);
      throw new Error(err.message || 'Failed to save API');
    }
  };

  const handleDelete = (api: ApiDetails) => {
    setSelectedApi(api);
    setConfirmAction(() => async () => {
      try {
        await apiAdmin.deleteApi(api.id!);
        await fetchApis();
      } catch (e: any) {
        setError(e.message || 'Failed to delete API');
      }
      setShowConfirmModal(false);
    });
    setShowConfirmModal(true);
  };

  const handleStatusToggle = async (api: ApiDetails) => {
    try {
      if (api.status === 'active') {
        await apiAdmin.deactivateApi(api.id!);
      } else {
        await apiAdmin.activateApi(api.id!);
      }
      await fetchApis();
    } catch (err: any) {
      setError(err.message || 'Failed to update API status');
    }
  };

  const getSortedApis = () => {
    return [...apis].sort((a, b) => {
      const aValue = (a as any)[sortField];
      const bValue = (b as any)[sortField];
      
      if (aValue < bValue) return sortDirection === 'asc' ? -1 : 1;
      if (aValue > bValue) return sortDirection === 'asc' ? 1 : -1;
      return 0;
    });
  };

  const getFilteredApis = () => {
    let filtered = getSortedApis();
    
    // Filter by organization
    if (selectedOrganization !== 'all') {
      filtered = filtered.filter(api => 
        (api as any).organization?.id?.toString() === selectedOrganization
      );
    }
    
    // Filter by search parameter type
    if (selectedParameterType !== 'all') {
      if (selectedParameterType === 'universal') {
        // Show only universal APIs
        filtered = filtered.filter(api => 
          (api as any).isUniversalSearch || (api as any).is_universal_search
        );
      } else {
        // Show APIs for specific parameter type
        filtered = filtered.filter(api => {
          const paramTypeId = (api as any).search_parameter_type?.id || 
                            (api as any).searchParameterType?.id ||
                            (api as any).search_parameter_type_id ||
                            (api as any).searchParameterTypeId;
          return paramTypeId?.toString() === selectedParameterType;
        });
      }
    }
    
    return filtered;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
      <PageHeader
        title="API Management"
        subtitle="Integrate any third-party API via cURL, RapidAPI, templates, or manual config — then test and auto-map response fields"
        align="center"
      >
        <MasterDataImportExport resource="apis" onComplete={fetchApis} />
      </PageHeader>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {error && (
          <div className="mb-6 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-md p-4">
            <p className="text-sm text-red-600 dark:text-red-400">{error}</p>
          </div>
        )}

        <div className="mb-6 flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
          <div className="flex items-center space-x-4">
            <Button onClick={handleCreate}>
              Create New API
            </Button>
          </div>

          <div className="flex flex-wrap items-center gap-4">
            <select
              value={selectedOrganization}
              onChange={(e) => setSelectedOrganization(e.target.value)}
              className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md text-sm"
            >
              <option value="all">All Organizations</option>
              {organizations.map(org => (
                <option key={org.id} value={org.id.toString()}>
                  {org.name}
                </option>
              ))}
            </select>
            
            <select
              value={selectedParameterType}
              onChange={(e) => setSelectedParameterType(e.target.value)}
              className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md text-sm"
            >
              <option value="all">All Parameter Types</option>
              <option value="universal">🌐 Universal APIs</option>
              {searchParameters.map(param => (
                <option key={param.id} value={param.id.toString()}>
                  {param.display_name}
                </option>
              ))}
            </select>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-6">
          {getFilteredApis().map(api => (
            <Card key={api.id} className="p-6">
              <div className="space-y-4">
                <div className="flex items-start justify-between">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <h3 className="text-lg font-semibold text-gray-900 dark:text-white">{api.name}</h3>
                      {(api as any).isUniversalSearch || (api as any).is_universal_search ? (
                        <Badge variant="info" className="flex-shrink-0">
                          🌐 Universal
                        </Badge>
                      ) : null}
                    </div>
                    <p className="text-sm text-gray-600 dark:text-gray-300 mt-1 break-words whitespace-pre-line">{api.description}</p>
                  </div>
                  <Badge variant={api.status === 'active' ? 'success' : api.status === 'inactive' ? 'danger' : 'warning'} className="ml-2 flex-shrink-0">
                    {api.status}
                  </Badge>
                </div>
                
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <span className="font-medium text-gray-700 dark:text-gray-200">Parameter Type:</span>
                    <p className="text-gray-900 dark:text-white break-words whitespace-pre-line">
                      {(api as any).isUniversalSearch || (api as any).is_universal_search 
                        ? '🌐 All Types (Universal)' 
                        : (api.searchParameterType?.display_name || (api as any).search_parameter_type?.display_name || 'N/A')}
                    </p>
                  </div>
                  <div>
                    <span className="font-medium text-gray-700 dark:text-gray-200">Price:</span>
                    <p className="text-gray-900 dark:text-white">{api.price} coins</p>
                  </div>
                  <div>
                    <span className="font-medium text-gray-700 dark:text-gray-200">Method:</span>
                    <p className="text-gray-900 dark:text-white">{api.requestMethod || 'N/A'}</p>
                  </div>
                  <div>
                    <span className="font-medium text-gray-700 dark:text-gray-200">Organization:</span>
                    <p className="text-gray-900 dark:text-white">{(api as any).organization?.name || 'N/A'}</p>
                  </div>
                </div>
                
                {/* API Documentation URL */}
                {(api as any).apiDocumentationUrl || (api as any).api_documentation_url ? (
                  <div className="pt-2">
                    <span className="font-medium text-gray-700 dark:text-gray-200 text-sm">Documentation:</span>
                    <a 
                      href={(api as any).apiDocumentationUrl || (api as any).api_documentation_url} 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="text-indigo-600 dark:text-indigo-400 hover:text-indigo-800 text-sm break-all block mt-1"
                    >
                      {(api as any).apiDocumentationUrl || (api as any).api_documentation_url}
                    </a>
                  </div>
                ) : null}
                
                <div className="flex flex-wrap gap-2 pt-2">
                  <Button 
                    variant="outline" 
                    size="sm" 
                    onClick={() => handleEdit(api)}
                    disabled={loading}
                  >
                    {loading ? 'Loading...' : 'Edit'}
                  </Button>
                  <Button variant="outline" size="sm" onClick={() => {
                    setSelectedApi(api);
                    setShowTestModal(true);
                    setTestResult(null);
                  }}>
                    Test
                  </Button>
                  <Button variant="outline" size="sm" onClick={() => handleAPITest(api)}>
                    Test API
                  </Button>
                  <Button variant="outline" size="sm" onClick={() => handleClone(api)}>
                    Clone for Other Type
                  </Button>
                  <Button variant="outline" size="sm" onClick={() => {
                    setSelectedApi(api);
                    setShowFieldMappingModal(true);
                  }}>
                    Field Mappings
                  </Button>
                  <ToggleSwitch
                    checked={api.status === 'active'}
                    onChange={() => handleStatusToggle(api)}
                    label={api.status === 'active' ? 'Active' : 'Inactive'}
                  />
                  {((api as any).allowRegularUsers || (api as any).allow_regular_users) && (
                    <Badge variant="secondary">Regular users</Badge>
                  )}
                  <Button variant="danger" size="sm" onClick={() => handleDelete(api)}>
                    Delete
                  </Button>
                </div>
              </div>
            </Card>
          ))}
        </div>

        {getFilteredApis().length === 0 && (
          <div className="text-center py-12">
            <p className="text-gray-500 dark:text-gray-400 text-lg">No APIs found</p>
            <Button onClick={handleCreate} className="mt-4">
              Create Your First API
            </Button>
          </div>
        )}
      </div>

      <EditModal
        api={selectedApi}
        onSave={handleSave}
        onClose={() => setShowEditModal(false)}
        isOpen={showEditModal}
        onImportSuccess={async (apiData) => {
          // Fetch fresh data and update the Edit modal
          try {
            const freshApi = await apiAdmin.getApi(apiData.id!);
            setSelectedApi(freshApi);
            // Also add to the API list
            setApis(prevApis => [...prevApis, freshApi]);
          } catch (err) {
            console.error('Failed to fetch imported API:', err);
          }
        }}
        onSwitchTab={(tab) => {
          // This will be handled inside EditModal's setActiveTab
        }}
      />

      <EnhancedTestModal
        api={selectedApi}
        onTest={async () => {}}
        onClose={() => setShowTestModal(false)}
        isOpen={showTestModal}
        testResult={testResult}
      />

      <APITestModal
        api={selectedApi}
        isOpen={showAPITestModal}
        onClose={() => setShowAPITestModal(false)}
      />

      <ConfirmModal
        isOpen={showConfirmModal}
        title="Delete API"
        message={`Are you sure you want to delete "${selectedApi?.name}"? This action cannot be undone.`}
        onConfirm={() => confirmAction?.()}
        onCancel={() => setShowConfirmModal(false)}
      />

      {showFieldMappingModal && selectedApi && (
        <FieldMappingManager
          apiId={selectedApi.id!}
          onClose={() => setShowFieldMappingModal(false)}
        />
      )}

      {showCloneModal && selectedApi && (
        <CloneApiModal
          isOpen={showCloneModal}
          sourceApi={selectedApi}
          availableParameterTypes={searchParameters}
          existingApis={apis}
          onClose={() => {
            setShowCloneModal(false);
            setSelectedApi(null);
          }}
          onClone={handleCloneSubmit}
        />
      )}
    </div>
  );
};

export default AdminApiManagementPage;
